import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Box, CircularProgress, FormLabel, RadioGroup } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs, { type Dayjs } from 'dayjs'

import { apiZodios } from '~api/apiZodios'
import { Button, Chip, Icon, Radio, TextField, Toast, Typography } from '~components/internal'
import Dropdown, { type DropdownOption } from '~components/internal/Dropdown'
import RichTextEditorComponent from '~components/internal/RichTextEditor'
import { type ContactView } from '~pages/ConstituentScreens/components/formData'
import AddressInputTable from '~pages/ContactsScreen/components/AddressInputTable/AddressInputTable'
import AttributionInputTable from '~pages/ContactsScreen/components/AttributionInputTable/AttributionInputTable'
import EmailInputTable from '~pages/ContactsScreen/components/EmailInputTable/cmv3/EmailInputTable'
import PhoneInputTable from '~pages/ContactsScreen/components/PhoneInputTable/cmv3/PhoneInputTable'
import { type RootState } from '~redux/rootReducer'
import { typography } from '~tokens/internal'
import {
  type Address0,
  type AttributionCreate,
  type ContactBook,
  type GeoPoint,
  type IdentityId,
  type TagData,
} from '~types'

import '../AddContactDialog.scss'
import useAddContactFormValidation from '../AddContactFormValidation'

// TODO: Address0 requires id/location_id/contact_id which ContactPage doesn't
// have for pre-existing addresses; we generate dummy values below. Follow-up
// PR should decouple this form from Address0 entirely.
type ContactFormAddress = Omit<Address0, 'id' | 'location_id' | 'contact_id' | 'location'> & {
  id?: string
  location_id?: string
  contact_id?: string | null
  location?: GeoPoint | null
}

export interface ContactFormData {
  first_name?: string
  last_name?: string
  date_of_birth?: string | null
  emails?: string[]
  phones?: string[]
  addresses?: ContactFormAddress[]
  tags?: string[]
  attributions?: string[][]
  notes?: string
  is_organization?: boolean
}

interface AddContactFormControlProps {
  contactBook?: ContactBook | null
  organizationIdentityIdList?: IdentityId[]
  availableTags?: TagData[]
  onClose?: () => void
  onComplete?: (createdContact?: ContactView) => void
  isSaved?: boolean
  setIsSaved?: (isSaved: boolean) => void
  onFormValidChange?: (isValid: boolean) => void
  update?: boolean
  onUpdate?: (formData: ContactFormData) => Promise<void>
  initialData?: ContactFormData
}

const AddContactFormControl: React.FC<AddContactFormControlProps> = ({
  contactBook,
  organizationIdentityIdList,
  availableTags = [],
  onClose,
  onComplete,
  isSaved = false,
  setIsSaved,
  onFormValidChange,
  update = false,
  onUpdate,
  initialData,
}) => {
  const user = useSelector((state: RootState) => state?.auth.user)

  const [firstName, setFirstName] = useState<string>(initialData?.first_name || '')
  const [lastName, setLastName] = useState<string>(initialData?.last_name || '')
  const [dateOfBirth, setDateOfBirth] = useState<Dayjs | null>(
    initialData?.date_of_birth ? dayjs(initialData.date_of_birth) : null,
  )
  const [notes, setNotes] = useState<string>(initialData?.notes || '')
  const [emails, setEmails] = useState<string[]>(initialData?.emails || [])
  const [phoneNumbers, setPhoneNumbers] = useState<string[]>(initialData?.phones || [])
  const [addresses, setAddresses] = useState<Address0[]>(
    (initialData?.addresses || []).map((address) => ({
      ...address,
      id: address.id || crypto.randomUUID(),
      location_id: address.location_id || crypto.randomUUID(),
      contact_id: address.contact_id ?? null,
      location: address.location || { lon: 0, lat: 0 },
    })),
  )

  const initialTags = (initialData?.tags || [])
    .map((tagName) => availableTags.find((t) => t.name === tagName))
    .filter((t): t is TagData => t !== undefined)

  const [tags, setTags] = useState<TagData[]>(initialTags)
  const [attributions, setAttributions] = useState<AttributionCreate[]>(
    (initialData?.attributions || []).map(([name, value]) => ({ name, value, attribute_id: null })),
  )
  const [isOrganization, setIsOrganization] = useState<boolean>(
    initialData?.is_organization ?? false,
  )
  const [isAddingContact, setIsAddingContact] = useState<boolean>(false)
  const [isEmailsValid, setIsEmailsValid] = useState<boolean>(true)
  const [isPhonesValid, setIsPhonesValid] = useState<boolean>(true)
  const [isAttributesValid, setIsAttributesValid] = useState<boolean>(true)
  const [toast, setToast] = useState<{
    open: boolean
    message: string
    type: 'error' | 'warning'
  }>({ open: false, message: '', type: 'error' })
  const [existingContact, setExistingContact] = useState<{
    handle: string
    bookCode: string
  } | null>(null)

  const { validationState, isBasicInfoValid, handleBlur, shouldShowError, resetValidation } =
    useAddContactFormValidation(firstName, lastName, isOrganization)

  const isFormInvalid = !isBasicInfoValid || !isEmailsValid || !isPhonesValid || !isAttributesValid

  const tagOptions: DropdownOption[] = availableTags.map((tag) => ({
    value: tag.id,
    label: tag.name,
    chip: (
      <Chip
        label={tag.name}
        color={tag.color.startsWith('#') ? tag.color : `#${tag.color}`}
        size='small'
      />
    ),
  }))

  const handleTagsChange = (value: string | string[]): void => {
    const ids = Array.isArray(value) ? value : [value]
    setTags(availableTags.filter((tag) => ids.includes(tag.id)))
  }

  const handleClose = useCallback((): void => {
    setFirstName('')
    setLastName('')
    setDateOfBirth(null)
    setEmails([])
    setPhoneNumbers([])
    setAddresses([])
    setTags([])
    setAttributions([])
    setToast({ open: false, message: '', type: 'error' })
    setIsSaved?.(false)
    resetValidation()
    onClose?.()
  }, [onClose, resetValidation, setIsSaved])

  const handleSaveContact = useCallback(async (): Promise<void> => {
    if (!user || (!update && !contactBook) || isAddingContact) return

    setToast({ open: false, message: '', type: 'error' })
    setIsAddingContact(true)

    if (update && onUpdate) {
      try {
        await onUpdate({
          first_name: firstName,
          last_name: isOrganization ? '' : lastName,
          date_of_birth: isOrganization ? null : dateOfBirth?.format('YYYY-MM-DD') || null,
          is_organization: isOrganization,
          emails,
          phones: phoneNumbers,
          addresses: addresses,
          tags: tags.map((t) => t.id),
          attributions: attributions.map((a) => [a.name, a.value]),
          notes,
        })
        setIsAddingContact(false)
        onComplete?.()
        handleClose()
      } catch {
        setToast({
          open: true,
          message: 'An error occurred while updating the contact.',
          type: 'error',
        })
        setIsSaved?.(false)
        setIsAddingContact(false)
      }
      return
    }

    if (!contactBook) return

    try {
      const response = await apiZodios.contacts_createContactRecord({
        user_id: user.id,
        organization_identity_id_list: organizationIdentityIdList ?? [],
        contact_book_id: contactBook.id,
        is_organization: isOrganization,
        first_name: firstName,
        last_name: isOrganization ? '' : lastName,
        date_of_birth: isOrganization ? null : (dateOfBirth?.format('YYYY-MM-DD') ?? null),
        emails,
        phones: phoneNumbers,
        addresses: addresses.map((address) => address.id),
        tags: tags.map((tag) => tag.id),
        attributions: attributions.map((a) => ({
          attribute_id: null,
          name: a.name,
          value: a.value,
        })),
      })

      setIsAddingContact(false)

      if (response.existing_contact_handle) {
        setExistingContact({ handle: response.existing_contact_handle, bookCode: contactBook.code })
        setToast({
          open: true,
          message: 'A contact with this email or phone number already exists in this contact book.',
          type: 'warning',
        })
        setIsSaved?.(false)
        return
      }

      if (!response.contact_record_id || !response.contact_record_handle) {
        setToast({
          open: true,
          message: 'An error occurred while saving the contact.',
          type: 'error',
        })
        setIsSaved?.(false)
        setIsAddingContact(false)
        return
      }

      onComplete?.({
        id: response.contact_record_id,
        contact_book_id: contactBook.id,
        handle: response.contact_record_handle,
        contact_type: isOrganization ? 'organization' : 'person',
        first_name: firstName || null,
        last_name: isOrganization ? '' : lastName || null,
        date_of_birth: isOrganization ? null : (dateOfBirth?.format('YYYY-MM-DD') ?? null),
        emails,
        phones: phoneNumbers,
        addresses: addresses.map((address) => ({
          street: address.street,
          city: address.city,
          state: address.state,
          zip: address.zip,
        })),
        tags: [],
        attributions: [],
        interaction_count: 0,
      })
      handleClose()
    } catch {
      setToast({
        open: true,
        message: 'An error occurred while saving the contact.',
        type: 'error',
      })
      setIsSaved?.(false)
      setIsAddingContact(false)
    }
  }, [
    user,
    contactBook,
    isAddingContact,
    organizationIdentityIdList,
    isOrganization,
    firstName,
    lastName,
    dateOfBirth,
    emails,
    phoneNumbers,
    addresses,
    tags,
    attributions,
    notes,
    update,
    onUpdate,
    onComplete,
    handleClose,
    setIsSaved,
  ])

  useEffect(() => {
    onFormValidChange?.(isFormInvalid)
  }, [isFormInvalid, onFormValidChange])

  useEffect(() => {
    if (isSaved && !isFormInvalid) {
      handleSaveContact()
    }
  }, [isSaved, isFormInvalid, handleSaveContact])

  return (
    <Box component='form'>
      {isAddingContact && (
        <Box className='add-contact-dialog__overlay'>
          <CircularProgress size={typography.fontSize.mega} />
          <Typography className='add-contact-dialog__overlay-text'>
            {update ? 'Updating contact...' : 'Adding contact...'}
          </Typography>
        </Box>
      )}
      <Box className='add-contact-dialog__form'>
        <Box className='add-contact-dialog__type-selector'>
          <FormLabel>Type of Contact</FormLabel>
          <RadioGroup
            row
            value={isOrganization}
            onChange={(e) => setIsOrganization(e.target.value === 'true')}
          >
            <Radio label='Individual' value={false} />
            <Radio label='Organization' value={true} />
          </RadioGroup>
        </Box>

        <Box className='add-contact-dialog__fields'>
          <Box className='add-contact-dialog__field'>
            <TextField
              label={isOrganization ? 'Organization Name' : 'First Name'}
              required
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              onBlur={() => handleBlur('firstName')}
              error={shouldShowError('firstName')}
              errorText={validationState.firstName.error}
            />
          </Box>
          {!isOrganization && (
            <Box className='add-contact-dialog__field'>
              <TextField
                label='Last Name'
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                onBlur={() => handleBlur('lastName')}
                error={shouldShowError('lastName')}
                errorText={validationState.lastName.error}
              />
            </Box>
          )}
        </Box>

        {!isOrganization && (
          <Box className='add-contact-dialog__field'>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label='Date of Birth'
                value={dateOfBirth}
                onChange={(newValue) => setDateOfBirth(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
        )}

        <EmailInputTable
          initialEmails={emails}
          setEmails={setEmails}
          onValidationChange={setIsEmailsValid}
        />
        <PhoneInputTable
          initialPhoneNumbers={phoneNumbers}
          setPhoneNumbers={setPhoneNumbers}
          onValidationChange={setIsPhonesValid}
        />
        <AddressInputTable
          addresses={addresses}
          setAddresses={setAddresses}
          organizationId={organizationIdentityIdList?.[0]}
        />
        <AttributionInputTable
          initialAttributions={attributions}
          setAttributions={setAttributions}
          onValidationChange={setIsAttributesValid}
        />

        <Box className='add-contact-dialog__field'>
          <Dropdown
            label='Tags'
            options={tagOptions}
            value={tags.map((t) => t.id)}
            onChange={handleTagsChange}
            multiple
            searchable
            placeholder='Select tags...'
            fullWidth
          />
        </Box>

        {update && (
          <Box className='add-contact-dialog__field'>
            <FormLabel>Notes</FormLabel>
            <RichTextEditorComponent
              className='add-contact-notes-editor'
              initialContent={notes}
              onChange={(htmlContent) => setNotes(htmlContent)}
            />
          </Box>
        )}
      </Box>

      <Toast
        type={toast.type}
        message={toast.message}
        open={toast.open}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        action={
          existingContact ? (
            <Button
              variant='text'
              startIcon={<Icon name='person' />}
              className='add-contact-dialog__toast-action'
              onClick={() => {
                window.open(
                  `/contacts/${existingContact.bookCode}/${existingContact.handle}`,
                  '_blank',
                )
              }}
            >
              View Contact
            </Button>
          ) : undefined
        }
      />
    </Box>
  )
}

export default AddContactFormControl
